import type {
  DashboardLayoutResponse,
  DashboardUser,
  DashboardWidgetDataMap,
  DashboardWidgetKey,
} from "@repo/shared/types";
import { useQuery } from "@tanstack/react-query";
import {
  type DashboardTarget,
  type DashboardWidgetOptions,
  getDashboardLayout,
  getDashboardUsers,
  getDashboardWidget,
} from "@/lib/api/dashboard";
import { useUserLanguage } from "@/lib/i18n";

/** Everything under ["dashboard"] refreshes together; settings changes invalidate it. */
export const dashboardKeys = {
  all: ["dashboard"] as const,
  layout: (target: DashboardTarget) => ["dashboard", "layout", target] as const,
  widget: (key: DashboardWidgetKey, target: DashboardTarget, options: DashboardWidgetOptions) =>
    ["dashboard", "widget", key, target, options] as const,
  users: ["dashboard", "users"] as const,
};

export function useDashboardLayout(target: DashboardTarget) {
  const { data, error, isLoading } = useQuery({
    queryKey: dashboardKeys.layout(target),
    queryFn: () => getDashboardLayout(target),
  });
  return { layout: (data as DashboardLayoutResponse | undefined) ?? null, error, isLoading };
}

/**
 * One widget's data. Switching its period or sort keeps the previous data on
 * screen until the new data arrives, so the widget doesn't flash empty — but
 * never across dashboards or users, which would show one user's figures under
 * another's name.
 */
export function useDashboardWidget<K extends DashboardWidgetKey>(
  key: K,
  target: DashboardTarget,
  options: Omit<DashboardWidgetOptions, "language"> = {},
) {
  const { userLanguage } = useUserLanguage();
  const fullOptions = { ...options, language: userLanguage };
  const { data, error, isLoading } = useQuery({
    queryKey: dashboardKeys.widget(key, target, fullOptions),
    queryFn: () => getDashboardWidget(key, target, fullOptions),
    placeholderData: (previous, previousQuery) => {
      const previousTarget = previousQuery?.queryKey[3] as DashboardTarget | undefined;
      return previousTarget?.dashboard === target.dashboard &&
        previousTarget?.forUserId === target.forUserId
        ? previous
        : undefined;
    },
  });
  const widgetData = data ? (data.data as DashboardWidgetDataMap[K]) : null;
  return { data: widgetData, error, isLoading };
}

export function useDashboardTenantUsers() {
  const { data, error, isLoading } = useQuery({
    queryKey: dashboardKeys.users,
    queryFn: () => getDashboardUsers(),
  });
  return { tenantUsers: (data ?? []) as DashboardUser[], error, isLoading };
}
